/**
 * lazy-load.ts
 *
 * Network-resilient lazy loading for Angular route definitions.
 *
 * Designed for poor / flaky networks, where the failure mode is usually a
 * request that *stalls* (the dynamic import() never settles and the UI looks
 * frozen) or one that fires during a brief offline blip. It layers three things
 * on top of a plain import():
 *
 *   1. Per-attempt timeout that escalates each retry (6s → 12s → 24s → 30s…),
 *      so a stalled load is abandoned and retried instead of hanging forever.
 *      No network "inference" is used — the schedule discovers how patient to be.
 *   2. Pause-while-offline + exponential backoff with jitter between attempts.
 *   3. A one-time hard refresh as a last resort, which also self-heals the
 *      "stale chunk after a new deploy" case (old hashed file is gone → retrying
 *      the same URL can't help, but a refresh pulls the new app shell).
 *
 * ---------------------------------------------------------------------------
 * USAGE (routes)
 * ---------------------------------------------------------------------------
 *   import { lazyLoad } from './lazy-load';
 *
 *   export const routes: Routes = [
 *     {
 *       path: 'dashboard',
 *       loadComponent: () =>
 *         lazyLoad(() => import('./dashboard/dashboard.component'))
 *           .then((m) => m.DashboardComponent),
 *     },
 *     {
 *       path: 'reports',
 *       loadChildren: () =>
 *         lazyLoad(() => import('./reports/reports.routes'))
 *           .then((m) => m.REPORTS_ROUTES),
 *     },
 *   ];
 *
 * ---------------------------------------------------------------------------
 * OPTIONAL: also clear the reload guard on a successful navigation, so a chunk
 * error that surfaces OUTSIDE these loaders (e.g. via a global ErrorHandler)
 * can still trigger one reload later. lazyLoad already clears it on its own
 * successful load, so this is just a belt-and-braces catch-all.
 * ---------------------------------------------------------------------------
 *   // in AppComponent (or an app-level provider)
 *   private router = inject(Router);
 *   constructor() {
 *     this.router.events.subscribe((e) => {
 *       if (e instanceof NavigationEnd) clearReloadFlag();
 *     });
 *   }
 */

const RELOAD_FLAG = 'chunkReloadAttempted';

export interface LazyLoadOptions {
  /** Retries after the first attempt. Total attempts = retries + 1. Default 4. */
  retries?: number;
  /** Base delay (ms) for exponential backoff between attempts. Default 800. */
  baseDelay?: number;
  /** Timeout (ms) for the first attempt; doubles each retry up to maxTimeout. Default 6000. */
  firstTimeout?: number;
  /** Maximum per-attempt timeout (ms). Default 30000. */
  maxTimeout?: number;
}

const DEFAULTS: Required<LazyLoadOptions> = {
  retries: 4,
  baseDelay: 800,
  firstTimeout: 6000,
  maxTimeout: 30000,
};

/** Reject `promise` if it has not settled within `ms`. */
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`lazyLoad: attempt timed out after ${ms}ms`)),
      ms,
    );
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

/**
 * Resolve immediately if the device reports being online, otherwise wait for
 * the next `online` event.
 *
 * Note: `navigator.onLine === false` reliably means offline, so pausing on it is
 * sound. `onLine === true` does NOT prove a working connection (it only means a
 * network interface exists), so we never treat "online" as a guarantee — every
 * attempt is still guarded by `withTimeout`. This only avoids burning attempts
 * while the device is definitively offline.
 */
function waitForOnline(): Promise<void> {
  if (typeof navigator === 'undefined' || navigator.onLine) {
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => {
    const handler = () => {
      window.removeEventListener('online', handler);
      resolve();
    };
    window.addEventListener('online', handler);
  });
}

const sleep = (ms: number): Promise<void> =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

function hasSessionStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

/**
 * Clear the reload guard. Called automatically after any successful load, and
 * safe to call manually on Router `NavigationEnd`. Clearing it means a *future*
 * chunk failure is allowed to trigger one reload again.
 */
export function clearReloadFlag(): void {
  if (hasSessionStorage()) {
    window.sessionStorage.removeItem(RELOAD_FLAG);
  }
}

/**
 * Load a lazy chunk with retries, escalating timeouts and a one-time hard
 * refresh fallback. See file header for details and usage.
 *
 * @param loader  The dynamic import, e.g. `() => import('./x.component')`.
 * @param options Tuning knobs; sensible defaults target poor networks.
 */
export async function lazyLoad<T>(
  loader: () => Promise<T>,
  options: LazyLoadOptions = {},
): Promise<T> {
  const { retries, baseDelay, firstTimeout, maxTimeout } = { ...DEFAULTS, ...options };

  for (let attempt = 0; attempt <= retries; attempt++) {
    // Escalate patience each attempt rather than trying to guess the network.
    const timeout = Math.min(firstTimeout * 2 ** attempt, maxTimeout);

    try {
      await waitForOnline();
      const result = await withTimeout(loader(), timeout);

      // Success: a timed-out earlier attempt's fetch may have completed in the
      // background and been cached, which is why a later attempt can resolve
      // instantly. Reset the guard so a future failure can reload again.
      clearReloadFlag();
      return result;
    } catch (error) {
      const isLastAttempt = attempt === retries;

      if (isLastAttempt) {
        // Every attempt failed. A hard refresh re-fetches the latest index.html
        // and chunk hashes (fixing stale-deploy chunks too). Guard against
        // reload loops: only reload once until a navigation succeeds, since
        // sessionStorage survives a reload within the same tab.
        if (hasSessionStorage() && !window.sessionStorage.getItem(RELOAD_FLAG)) {
          window.sessionStorage.setItem(RELOAD_FLAG, '1');
          window.location.reload();
        }
        throw error;
      }

      // Exponential backoff + jitter so retries don't hammer a congested link
      // and concurrent loads don't sync up.
      const delay = baseDelay * 2 ** attempt + Math.random() * 300;
      await sleep(delay);
    }
  }

  // Unreachable (loop either returns or throws on the last attempt), but keeps
  // TypeScript's control-flow analysis satisfied about the return type.
  throw new Error('lazyLoad: exhausted all attempts');
}